<template>
  <form @submit.prevent="onSubmit">
    <!-- delete confirm -->
    <confirm-dialog ref="confirmDialog"></confirm-dialog>
    <rune-linked-values-dialog
      ref="runeLinkedValuesDialog"
    ></rune-linked-values-dialog>
    <!-- ----------------- -->
    <!-- TOP ROW (HEADER)  -->
    <!-- ----------------- -->
    <div class="row h-40">
      <p class="bb-page-title mt-2">RUNE DETAILS</p>
    </div>
    <!-- -------------------------------------------------------------------------------- -->
    <!-- MAIN CONTAINER -->
    <!-- -------------- -->
    <div
      v-if="isReady"
      class="row mt-1 mb-2 m-2"
    >
      <!-- -------------------------------------------------------------------------------- -->
      <!--    LEFT COL    -->
      <!-- -------------- -->
      <div class="col-3">
        <!-- ---------- -->
        <!-- BUTTONS    -->
        <!-- ---------- -->
        <div class="row mt-2">
          <div
            class="btn-group-vertical btn-group-lg"
            role="group"
          >
            <!-- ------------ -->
            <!--    cancel    -->
            <!-- ------------ -->
            <button
              class="btn btn-lg btn-warning"
              type="button"
              @click="manageCancel()"
            >
              Cancel
            </button>
            <!-- ------------ -->
            <!--    delete    -->
            <!-- ------------ -->
            <button
              v-if="!isNewRune && !this.delete"
              class="btn btn-primary btn-lg"
              @click="confirmDeletion()"
            >
              Delete
            </button>
            <!-- ------------ -->
            <!--     add      -->
            <!-- ------------ -->
            <button
              v-if="isNewRune && activeRune.name != ''"
              :disabled="!hasLinkedItem"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              Add
            </button>
            <!-- ------------ -->
            <!--      ok      -->
            <!-- ------------ -->
            <button
              v-if="!isNewRune"
              :disabled="!isChanged || !hasLinkedItem"
              class="btn btn-primary btn-lg"
              type="submit"
            >
              OK
            </button>
          </div>
        </div>
      </div>
      <!-- -------------------------------------------------------------------------------- -->
      <!--    RIGHT COL   -->
      <!-- -------------- -->
      <div class="col m-3 bb-tooltip-wrapper">
        <!-- -------------- -->
        <!--      ROW 1     -->
        <!-- -------------- -->
        <div class="row mb-2">
          <!-- --------------------- -->
          <!--   row number (r/o)    -->
          <!-- --------------------- -->
          <div
            v-if="hasRuneName"
            class="col-3"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control text-center"
                  v-model="rowNumber"
                  id="row"
                  disabled
                />
                <label
                  class="bb-float-label optional"
                  for="row"
                  >Row</label
                >
              </div>
            </div>
          </div>
          <!-- ------------------- -->
          <!--   rune name (r/o)  -->
          <!-- ------------------- -->
          <div
            v-if="activeRune.id !== 0"
            class="col"
          >
            <div class="form-group">
              <div class="form-floating">
                <input
                  readonly
                  class="form-control"
                  :value="activeRune.name"
                  id="name"
                  disabled
                />
                <label
                  :class="['bb-float-label', { optional: activeRune.id !== 0 }]"
                  for="name"
                  >Name</label
                >
              </div>
            </div>
          </div>
          <!-- --------------------- -->
          <!--   rune name (input)   -->
          <!-- --------------------- -->
          <div
            v-if="activeRune.id == 0"
            class="col"
          >
            <div class="form-group">
              <div class="form-floating">
                <v-select
                  :options="names"
                  class="form-control"
                  :value="activeRune.name"
                  @keydown="onKeyDown($event)"
                  @update:modelValue="onRuneSelect"
                  id="name"
                >
                  <template #search="{ attributes, events }">
                    <input
                      class="vs__search"
                      :required="!activeRune.name"
                      v-bind="attributes"
                      v-on="events"
                    />
                  </template>
                </v-select>
                <label
                  class="bb-float-label"
                  for="name"
                  >Rune Name</label
                >
              </div>
            </div>
          </div>
        </div>
        <!-- ---------------------------- -->
        <!--   WHERE RUNE NAME PRESENT   -->
        <!-- ---------------------------- -->
        <div v-if="hasRuneName">
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 2     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ---------------------- -->
            <!--   strength (counter)   -->
            <!-- ---------------------- -->
            <div class="col">
              <CounterEmit
                :pass_ref="'strength'"
                :pass_label="'Strength'"
                :pass_count="activeRune.strength"
                :pass_min="this.config.min_rune_strength"
                :pass_max="this.config.max_rune_strength"
                :pass_skip_zero="true"
                :pass_digits="calcCounterDigits(this.config.max_rune_strength)"
                @change="onCounterChanged"
              />
            </div>
            <!-- --------------------------- -->
            <!--   power storage (counter)   -->
            <!-- --------------------------- -->
            <div class="col">
              <CounterEmit
                :pass_ref="'pwr_storage'"
                :pass_label="'Power Storage'"
                :pass_count="activeRune.pwr_storage"
                :pass_min="this.config.min_pwr_storage"
                :pass_max="this.config.max_pwr_storage"
                :pass_digits="calcCounterDigits(this.config.max_pwr_storage)"
                @change="onCounterChanged"
              />
            </div>
          </div>
          <!-- spacer row -->
          <div class="row mt-1"></div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 3     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ------------------------- -->
            <!--   starter rune (switch)   -->
            <!-- ------------------------- -->
            <div class="col-auto pe-3">
              <enhanced-toggle
                style="
                  --toggleWidth: 260px;
                  --labelWidth: 160px;
                  --switchWidth: 90px;
                  --switchPos: 160px;
                  height: 50px;
                  border-radius: 10px;
                  background-color: white;
                "
                :styleOff="`default`"
                :styleOn="`primary`"
                :labelOn="`Starter`"
                :labelOff="`Acquired`"
                :checked="activeRune.starter"
                v-model="activeRune.starter"
                :key="`starter-${switchKey}`"
                @update:modelValue="handleExistingRuneToggle('starter', $event)"
              >
              </enhanced-toggle>
            </div>
            <!-- ----------------------- -->
            <!--   integrated (switch)   -->
            <!-- ----------------------- -->
            <div class="col-auto ps-3">
              <enhanced-toggle
                style="
                  --toggleWidth: 260px;
                  --labelWidth: 160px;
                  --switchWidth: 90px;
                  --switchPos: 160px;
                  height: 50px;
                  border-radius: 10px;
                  background-color: white;
                "
                :styleOff="`default`"
                :styleOn="`primary`"
                :labelOn="`Integrated`"
                :labelOff="`Unintegrated`"
                :checked="activeRune.integrated"
                v-model="activeRune.integrated"
                :key="`integrated-${switchKey}`"
                @update:modelValue="
                  handleExistingRuneToggle('integrated', $event)
                "
                :disabled="isIntegratedDisabled"
              >
              </enhanced-toggle>
            </div>
            <!-- ----------------------- -->
            <!--   active (switch)       -->
            <!-- ----------------------- -->
            <div class="col-auto ps-3">
              <enhanced-toggle
                style="
                  --toggleWidth: 260px;
                  --labelWidth: 160px;
                  --switchWidth: 90px;
                  --switchPos: 160px;
                  height: 50px;
                  border-radius: 10px;
                  background-color: white;
                "
                :styleOff="`default`"
                :styleOn="`primary`"
                :labelOn="`Active`"
                :labelOff="`Inactive`"
                :checked="activeRune.active"
                v-model="activeRune.active"
                :key="`active-${switchKey}`"
                @update:modelValue="handleExistingRuneToggle('active', $event)"
                :disabled="isActiveDisabled"
              >
              </enhanced-toggle>
            </div>
          </div>
          <!-- spacer row -->
          <div class="row mt-1"></div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 4     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ----------------- -->
            <!--   powers (r/o)    -->
            <!-- ----------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <textarea
                    id="powers"
                    name="powers"
                    type="text"
                    rows="1"
                    class="form-control bb-textarea-with-label bb-rune-compact-textarea"
                    disabled
                    v-model="activeRune.powers"
                    placeholder="..."
                    autocomplete="off"
                  />
                  <label
                    class="bb-float-label optional"
                    for="powers"
                    >Powers</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 5     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ----------------------- -->
            <!--   short powers (r/o)    -->
            <!-- ----------------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-left"
                    :value="activeRune.short_powers"
                    id="row"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="row"
                    >Short Description</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 6     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ---------------- -->
            <!--   trait (r/o)    -->
            <!-- ---------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <input
                    readonly
                    class="form-control text-left"
                    :value="displayTrait"
                    id="row"
                    disabled
                  />
                  <label
                    class="bb-float-label optional"
                    for="row"
                    >Trait</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 7     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ---------------- -->
            <!--   item (input)   -->
            <!-- ---------------- -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <v-select
                    :options="itemOptions"
                    label="display"
                    :reduce="reduceItem"
                    class="form-control rune-item-select"
                    :class="{
                      'is-invalid': errorFields.includes('character_item'),
                    }"
                    placeholder="select..."
                    v-model="activeRune.character_item"
                    id="item"
                    @update:modelValue="onLinkedItemChange"
                    @keydown="onKeyDown($event)"
                  />
                  <label
                    class="bb-float-label optional"
                    for="item"
                    >Linked Item</label
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- -------------------------------------------------------------------------------- -->
          <!--      ROW 8     -->
          <!-- -------------- -->
          <div class="row mt-2 mb-2">
            <!-- ------------------ -->
            <!--   notes (input)    -->
            <!-- ------------------ -->
            <div class="col">
              <div class="form-group">
                <div class="form-floating">
                  <textarea
                    id="notes"
                    name="notes"
                    type="text"
                    rows="1"
                    class="form-control bb-textarea-with-label bb-rune-notes-textarea bb-profile-notes-control"
                    v-model="activeRune.notes"
                    placeholder="..."
                    autocomplete="off"
                    @input="onNotesInput($event)"
                  />
                  <label
                    class="bb-float-label optional"
                    for="notes"
                    >Notes</label
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
        <!-- -------------------------------------------------------------------------------- -->
        <!--    ERRORS  -->
        <!-- ---------- -->
        <div
          v-if="errors.length > 0"
          class="row mt-2 mb-2"
        >
          <div class="form-group">
            <div
              v-for="err in errors"
              :key="err.id"
              class="bb-error"
            >
              {{ err }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </form>
</template>

<script>
import ConfirmDialog from '../components/ConfirmDialog.vue'
import RuneLinkedValuesDialog from '../components/RuneLinkedValuesDialog.vue'
import CounterEmit from './CounterEmit.vue'

import { mapGetters } from 'vuex'
import { inject } from 'vue'

import saveCurrentEditableRune from '../helpers/saveCurrentEditableRune'
import checkRuneIntegrity from '../helpers/checkRuneIntegrity'
import isObjectChanged from '../helpers/isObjectChanged'
import capitaliseFirstLetter from '../helpers/capitaliseFirstLetter'
import normaliseLeadingLetterText from '../helpers/normaliseLeadingLetterText'
import copyObjectNoRef from '../helpers/copyObjectNoRef'
import setRuneValues from '../helpers/setRuneValues'
import isAlphanumeric from '../helpers/isAlphanumeric'
import getRuneChanges from '../helpers/getRuneChanges'
import extractNames from '../helpers/extractNames'
import createTempId from '../helpers/createTempId'
import getLinkedRuneSpellEntries from '../helpers/getLinkedRuneSpellEntries'

export default {
  name: 'RuneCard',
  emits: [
    'add-character-rune',
    'update-character-rune',
    'delete-character-spell',
    'delete-character-rune',
    'close-rune-panel',
  ],
  //========================================================
  // components
  //------------
  components: {
    ConfirmDialog,
    RuneLinkedValuesDialog,
    CounterEmit,
  },
  //========================================================
  // setup
  //-------
  setup() {
    const global = inject('global')
    return { global }
  },
  //========================================================
  // props
  //-------
  props: {
    rune: {
      type: Object,
      default: null,
    },
    character: {
      type: Object,
      required: true,
      default: null,
    },
  },
  //========================================================
  // data
  //------
  data() {
    return {
      config: null,
      save_rune_id: null,
      //
      delete: false,
      //
      local_rune: {},
      original_rune: {},
      pendingTimers: {}, // per-field debounce timers (key -> timeout id)
      //
      names: [],
      errors: [],
      errorFields: [],
      available_runes: [],
      items: [],
      //
      switchKey: 0, // forcing switch re-render
    }
  },
  //========================================================
  // watch
  //-------
  watch: {
    rune: {
      deep: true,
      immediate: true,
      handler(newRune, oldRune) {
        console.log('=====================================')
        console.log('WATCH RUNE PROP CHANGE')
        if (!newRune) return
        //-------------------------------------------------------------
        // check if only property values changed (not array structure)
        //-------------------------------------------------------------
        if (oldRune && newRune.id === oldRune.id) {
          console.log('rune properties updated - syncing local_rune')
          //----------------------------------
          // sync changed props to local_rune
          //----------------------------------
          this.local_rune = { ...newRune }

          console.log(
            'AFTER emit - local_rune.active = ',
            this.local_rune.active,
          )
          console.log(
            'AFTER emit - activeRune.active = ',
            this.activeRune.active,
          )

          return
        }
        //-----------------
        // new rune object
        //-----------------
        console.log('new rune prop')
        this.local_rune = copyObjectNoRef(newRune) || {}
      },
    },
    character: {
      deep: true,
      immediate: true,
      handler(newCharacter) {
        console.log('=====================================')
        console.log('WATCH CHARACTER PROP CHANGE')
        if (!newCharacter) return
        //------------------------------------
        // load character items for dropdowns
        //------------------------------------
        this.items = newCharacter.items || []
      },
    },
  },
  //==========================================
  // computed
  //-----------
  computed: {
    // options for item dropdown: prefer per-instance display_name so
    // duplicate linked items (e.g. "Pouch 2") remain distinguishable.
    itemOptions() {
      if (!Array.isArray(this.items)) return []
      const sorted = [...this.items].sort((a, b) => {
        const typeA = (a.type || '').toString().toLowerCase()
        const typeB = (b.type || '').toString().toLowerCase()
        if (typeA < typeB) return -1
        if (typeA > typeB) return 1
        const nameA = (a.display_name || a.name || '').toString().toLowerCase()
        const nameB = (b.display_name || b.name || '').toString().toLowerCase()
        if (nameA < nameB) return -1
        if (nameA > nameB) return 1
        return 0
      })
      return sorted.map((i) => ({
        ...i,
        display: [i.type, i.display_name || i.name].filter(Boolean).join(' - '),
      }))
    },
    isIntegratedDisabled() {
      return !this.activeRune || this.activeRune.starter
    },
    isActiveDisabled() {
      return !this.activeRune || this.activeRune.integrated === false
    },
    isReady() {
      const s = this.activeRune || {}
      return !!this.config && s && Object.keys(s).length > 0
    },
    activeRune() {
      const hasLocal =
        this.local_rune && Object.keys(this.local_rune).length > 0
      return hasLocal ? this.local_rune : (this.rune ?? {})
    },
    isNewRune() {
      return this.activeRune.id === 0
    },
    isMaxStrength() {
      return (
        !this.activeRune ||
        this.activeRune.strength === this.config.max_rune_strength
      )
    },
    isMaxPwrStorage() {
      return (
        !this.activeRune ||
        this.activeRune.pwr_storage === this.config.max_rune_pwr_storage
      )
    },
    hasRuneName() {
      const s = this.activeRune
      if (!s || !s.name) return false
      return true
    },
    rowNumber() {
      return (this.activeRune?.originalIndex ?? 0) + 1
    },
    hasName() {
      const name = this.activeRune?.name ?? ''
      return name !== ''
    },
    hasLinkedItem() {
      return this.activeRune?.character_item != null
    },
    displayTrait() {
      const traitDisplay = (
        this.activeRune?.trait_name ||
        this.activeRune?.trait ||
        ''
      )
        .toString()
        .trim()
      if (traitDisplay !== '') {
        return traitDisplay
      }
      return 'None'
    },
    isChanged() {
      const currentEditable = saveCurrentEditableRune(this.activeRune || {})
      // console.log('isChanged this.activeRune:', this.activeRune)
      console.log('isChanged this.original_rune', this.original_rune)
      console.log('isChanged currentEditable', currentEditable)

      console.log(
        'isChanged this.original_rune.active =',
        this.original_rune.active,
      )
      console.log('isChanged currentEditable.active =', currentEditable.active)

      const changed = isObjectChanged(this.original_rune || {}, currentEditable)
      return changed
    },
  },
  //========================================================
  // methods
  //---------
  methods: {
    //========================================================
    // mapped
    //--------
    ...mapGetters('characters', {
      getRunes: 'getRunes',
    }),
    ...mapGetters('combat', {
      getConfig: 'getConfig',
    }),
    //========================================================
    // reduce item option -> id (for v-select)
    //-----------------------------------------
    reduceItem(option) {
      return option?.id ?? null
    },
    //========================================================
    // calc counter digits
    //---------------------
    calcCounterDigits(fieldMax) {
      // console.log('calcCounterDigits fieldMax = ', fieldMax)
      return fieldMax ? fieldMax.toString().length : 1
    },
    //========================================================
    // clear input error
    //-------------------
    clearInputError(field) {
      this.errorFields = (this.errorFields || []).filter((f) => f !== field)

      if (field === 'character_item') {
        const linkedItemMessage = window.bbUiErrorMsg(
          'Rune must be linked to an item',
        )
        this.errors = (this.errors || []).filter(
          (err) => err !== linkedItemMessage,
        )
      }
    },
    //========================================================
    // validate linked item
    //----------------------
    validateLinkedItem() {
      const hasLinkedItem = this.activeRune?.character_item != null

      this.clearInputError('character_item')

      if (hasLinkedItem) {
        return true
      }

      const message = window.bbUiErrorMsg('Rune must be linked to an item')
      if (!this.errors.includes(message)) {
        this.errors.push(message)
      }
      if (!this.errorFields.includes('character_item')) {
        this.errorFields.push('character_item')
      }

      return false
    },
    //========================================================
    // on key down
    //-------------
    onKeyDown(e) {
      if (!isAlphanumeric(e)) e.preventDefault()
    },
    //========================================================
    // on counter changed
    //--------------------
    onCounterChanged({ ref, count }) {
      console.log(
        'onCounterChanged ref = ',
        ref,
        ' count = ',
        count,
        ' new rune = ',
        this.isNewRune,
      )
      //--------------------------------
      // guard against undefined values
      //--------------------------------
      if (ref === undefined || count === undefined) {
        // console.warn('onCounterChanged: ignoring call with undefined ref or count')
        return
      }
      //--------------------------
      // ensure local rune exists
      //--------------------------
      this.local_rune = this.local_rune || {}
      this.local_rune[ref] = count
    },
    //========================================================
    // on rune select
    //-----------------
    onRuneSelect(name) {
      console.log('onRuneSelect name = ', name)

      if (!name) return
      this.local_rune = this.local_rune || {}
      //------------------------------------------------------------
      // get static rune for name selection (find -> single object)
      //------------------------------------------------------------
      const rune = this.available_runes.find((s) => s.name === name)
      if (!rune) {
        console.warn('onRuneSelect: rune not found for name', name)
        return
      }
      //--------------------------------
      // load selected rune name/powers
      //--------------------------------
      this.local_rune.name = rune.name
      this.local_rune.short_powers = rune.short_powers || ''
      this.local_rune.powers = rune.powers || ''
      this.local_rune.trait_name = rune.trait_name || ''
      this.local_rune.trait = rune.trait_name || ''
      //-------------------------
      // save id for add payload
      //-------------------------
      this.save_rune_id = rune.id

      if (this.isNewRune) {
        this.validateLinkedItem()
      }
    },
    //========================================================
    // get dropdown values
    //---------------------
    getDropDownValues() {
      // get runes rune names (can select existing rune name as can have multiples)
      this.available_runes = this.getRunes() || []
      this.names = extractNames(this.available_runes)
    },
    getLinkedSpellEntries() {
      return getLinkedRuneSpellEntries({
        character: this.character,
        rune: this.activeRune,
      })
    },
    hasPendingLinkedSpellDeletion() {
      if (this.isNewRune) {
        return false
      }

      return (
        (this.original_rune?.integrated === true &&
          this.activeRune?.integrated === false) ||
        (this.original_rune?.active === true &&
          this.activeRune?.active === false)
      )
    },
    revertPendingLinkedSpellDeletionChanges() {
      if (this.isNewRune || !this.local_rune) {
        return
      }

      if (
        this.original_rune?.integrated === true &&
        this.local_rune.integrated === false
      ) {
        this.local_rune.integrated = true
      }

      if (
        this.original_rune?.active === true &&
        this.local_rune.active === false
      ) {
        this.local_rune.active = true
      }

      this.$nextTick(() => {
        this.switchKey++
      })
    },
    async confirmLinkedRuneSpellDeletion() {
      const linkedSpellEntries = this.getLinkedSpellEntries()

      if (linkedSpellEntries.length === 0) {
        return true
      }

      const resp = await this.$refs.runeLinkedValuesDialog.show({
        title: 'Update Character Rune',
        message: 'Confirm rune changes and linked deletions:',
        runeName: this.activeRune?.name ?? '',
        details: linkedSpellEntries.map((entry) => entry.label),
        okButton: 'Save',
        cancelButton: 'Cancel',
      })

      if (resp?.cancel) {
        return false
      }

      linkedSpellEntries.forEach((entry) => {
        this.$emit('delete-character-spell', entry.spell)
      })

      return true
    },
    //=====================================================================
    // confirm deletion
    //------------------
    async confirmDeletion() {
      this.delete = true
      this.$emit('delete-character-rune', this.activeRune)
      this.delete = false
    },
    async handleExistingRuneToggle(key, value) {
      this.local_rune = this.local_rune || {}
      this.local_rune[key] = value

      const { updated, changes } = checkRuneIntegrity({
        rune: this.local_rune,
        changedField: key,
      })

      this.local_rune = updated

      if (changes.length > 0) {
        this.$nextTick(() => {
          this.switchKey++
        })
      }
    },
    //========================================================
    // on submit
    //-----------
    async onSubmit() {
      console.log('---------------------------------')
      console.log('onSubmit')
      //----------------------------------------------------------
      // EXISTING RUNES:
      // - edits stay local in RuneCard until OK
      // - linked spell delete confirmation runs only on OK
      // - delete is handled separately.
      //---------------------------------------------------------
      // NEW RUNES:
      // - changes are handled here on 'OK' submit
      //---------------------------------------------------------
      this.errors = []
      this.errorFields = []

      const hasLinkedItem = this.validateLinkedItem()

      if (this.errors.length > 0 || !hasLinkedItem) {
        return
      }

      if (!this.isNewRune && this.hasPendingLinkedSpellDeletion()) {
        const shouldContinue = await this.confirmLinkedRuneSpellDeletion()

        if (!shouldContinue) {
          this.revertPendingLinkedSpellDeletionChanges()
          return
        }
      }

      if (!this.isNewRune) {
        const currentEditable = saveCurrentEditableRune(this.activeRune || {})
        const changes = getRuneChanges({
          original: this.original_rune,
          now: currentEditable,
        })

        Object.entries(changes).forEach(([key, change]) => {
          this.emitUpdate(key, change.to)
        })

        this.closeSidePanel()
        return
      }

      //-----------------------
      // where adding new rune
      //-----------------------
      if (this.isNewRune) {
        //-----------------------
        // continue if no errors
        //-----------------------
        if (this.errors.length === 0 && hasLinkedItem) {
          console.log('onSubmit....')
          let item_name = null
          //---------------------
          // get editable fields
          //---------------------
          const strength = this.activeRune?.strength ?? 0
          const pwr_storage = this.activeRune?.pwr_storage ?? 0
          const starter = this.activeRune?.starter ?? false
          const integrated = this.activeRune?.integrated ?? false
          const active = this.activeRune?.active ?? false
          const character_item = this.activeRune?.character_item ?? null
          //----------------------------
          // get item name where linked
          //----------------------------
          if (character_item) {
            const linkedItem = this.items.find((it) => it.id === character_item)
            if (linkedItem) {
              item_name = linkedItem.name
            }
          }
          //--------------
          //set add parms
          //--------------
          const add_parms = {
            name: this.activeRune.name,
            payload: {
              id: createTempId(), // temp id used for tracking changes
              character: this.character.id ? this.character.id : null,
              rune: this.save_rune_id,
              strength: strength,
              pwr_storage: pwr_storage,
              starter: starter,
              integrated: integrated,
              active: active,
              character_item: character_item,
              item_name: item_name,
            },
          }
          //----------------
          // emit add event
          //----------------
          console.log('emitting add-character-rune with parms = ', add_parms)
          this.$emit('add-character-rune', add_parms)
        }
      }
      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },
    //========================================================
    // manage cancel
    //---------------
    manageCancel() {
      console.log('---------------------------------')
      console.log(
        'manageCancel isNewRune = ',
        this.isNewRune,
        ' isChanged = ',
        this.isChanged,
      )
      //------------------
      // close side panel
      //------------------
      this.closeSidePanel()
    },
    //========================================================
    // close side panel
    //------------------
    closeSidePanel() {
      this.$emit('close-rune-panel')
    },
    //========================================================
    // force initial focus
    //---------------------
    forceInitialFocus() {
      this.editingMode = true
      this.$nextTick(() => {
        this.$refs.input.focus()
      })
    },
    //========================================================
    // emit update
    //-------------
    emitUpdate(key, value) {
      //------------------------------
      // safety check for active rune
      //------------------------------
      if (!this.activeRune) return
      //------------------------------------------------------------
      // where new rune - only if switches (for checking integrity)
      //------------------------------------------------------------
      if (this.isNewRune) {
        // apply integrity rules for switches
        if (key === 'integrated' || key === 'active' || key === 'starter') {
          const { updated, changes } = checkRuneIntegrity({
            rune: this.local_rune,
            changedField: key,
          })
          this.local_rune = updated

          if (changes.length > 0) {
            console.log('Integrity applied to new rune:', changes)
            // force switch re-render to show any integrity changes
            this.$nextTick(() => {
              this.switchKey++
            })
          }
        }
        // For new runes we do not emit changes up to the
        // harness; all persistence happens in onSubmit().
        return
      }

      console.log('emitUpdate key = ', key, ' value = ', value)
      //------------------------------------------------
      // where existing rune - set args for update emit
      //------------------------------------------------
      const name = this.activeRune?.name ?? this.local_rune.name ?? '' // for debug
      const id = this.activeRune?.id ?? this.local_rune.id ?? null // for rune processing (id as could be multiple same name runes)
      const idx =
        this.activeRune?.originalIndex ?? this.local_rune.originalIndex ?? null // for force reactivity in parent
      console.log(
        'AAA this.activeRune?.originalIndex = ',
        this.activeRune?.originalIndex,
      )
      console.log(
        'BBB this.local_rune.originalIndex = ',
        this.local_rune.originalIndex,
      )
      console.log('CCC idx = ', idx)
      //-------------
      // emit update
      //-------------
      this.$emit('update-character-rune', {
        type: 'runes',
        key,
        value,
        name,
        id,
        idx,
      })
    },
    //========================================================
    // linked item change (dropdown)
    //--------------------------------
    onLinkedItemChange(value) {
      this.local_rune = this.local_rune || {}
      this.local_rune.character_item = value ?? null

      if (value != null) {
        this.clearInputError('character_item')
      } else {
        this.validateLinkedItem()
      }
    },
    //========================================================
    // on notes input
    //----------------
    onNotesInput(event) {
      if (!event || !event.target || !this.activeRune) return

      const value = normaliseLeadingLetterText(event.target.value ?? '')
      event.target.value = value
      this.activeRune.notes = value

      if (this.local_rune) {
        this.local_rune.notes = value
      }
    },
    //========================================================
    // prevent negative sign in number fields
    //----------------------------------------
    preventNegative(e) {
      // block minus, plus, decimal, and exponent keys
      const key = e.key
      if (
        key === '-' ||
        key === '+' ||
        key === 'Minus' ||
        key === 'Add' ||
        key === 'Subtract' ||
        key === '.' ||
        key === 'e' ||
        key === 'E'
      ) {
        e.preventDefault()
      }
    },
  },
  //========================================================
  // mounted
  //---------
  mounted() {
    //------------
    // get config
    //------------
    this.config = this.getConfig()
    //---------------------
    // get dropdown values
    //---------------------
    this.getDropDownValues()
    //----------------
    // existing rune
    //----------------
    if (this.rune) {
      console.log('EXISTING RUNE = ', this.rune)
      //-----------------------------------------------------------
      // copy rune to local rune for v-model (e.g. ticks/training)
      //-----------------------------------------------------------
      this.local_rune = copyObjectNoRef(this.rune) || {}
    } else {
      //-----------
      // new rune
      //-----------
      this.local_rune = setRuneValues()
      console.log('NEW RUNE...')
    }
    //-----------------------------------------
    // set strength/pwr_storage default values
    //-----------------------------------------
    if (this.local_rune.strength === 0 || this.local_rune.strength == null) {
      this.local_rune.strength = this.config.min_rune_strength
    }
    if (
      this.local_rune.pwr_storage === 0 ||
      this.local_rune.pwr_storage == null
    ) {
      this.local_rune.pwr_storage = this.config.min_pwr_storage
    }
    //-------------
    // set switches
    //-------------
    if (this.local_rune.starter == true) {
      this.local_rune.integrated = true
    }
    if (this.local_rune.integrated == false) {
      this.local_rune.active = false
    }
    //------------------------------------
    // save original for change detection
    //------------------------------------
    this.original_rune = saveCurrentEditableRune(this.local_rune) || {}
  },
  //========================================================
  // before unmount
  //----------------
  beforeUnmount() {
    // clear any outstanding timers
    Object.values(this.pendingTimers || {}).forEach((t) => clearTimeout(t))
    this.pendingTimers = {}
  },
}
</script>

<style scoped>
/* Keep the rune linked-item dropdown visually contained within the panel */
:deep(.rune-item-select .vs__dropdown-menu) {
  max-height: 240px;
  overflow-y: auto;
  padding-bottom: 6px;
}

.bb-rune-compact-textarea {
  resize: none;
}

.form-floating textarea.bb-rune-notes-textarea {
  min-height: 5.5em !important;
  height: 5.5em !important;
  padding-top: 2.3rem !important;
  padding-bottom: 0.3rem !important;
  line-height: 1.15 !important;
  resize: none;
  overflow-y: hidden;
}

.form-floating .bb-profile-notes-control {
  font-size: 1.4rem !important;
  line-height: 1.35 !important;
}
</style>
